import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Tag, ArrowRight } from "lucide-react";
import SEOHead from "../components/SEOHead";
import StoreSwitcher from "../components/StoreSwitcher";
import { getSpecials } from "../lib/api";
import { classifySpecial } from "../lib/promos";

function kindLabel(kind) {
    if (kind === "BOGO") return "BOGO";
    if (kind === "PCT_OFF") return "% Off";
    if (kind === "BUY_FOR") return "Bundle";
    if (kind === "DOLLAR_OFF") return "$ Off";
    return "Promotion";
}

export default function SpecialsDirectory() {
    const { storeSlug: paramSlug } = useParams();
    const storeSlug = paramSlug || "queens-long-island";
    const [specials, setSpecials] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const applyData = (d) => {
            if (!cancelled) {
                setSpecials(d?.specials || []);
                setErr(null);
            }
        };
        const applyErr = () => {
            if (!cancelled) setErr("error");
        };
        getSpecials(storeSlug).then(applyData).catch(applyErr);
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    const rows = useMemo(() => {
        if (!specials) return [];
        return specials
            .map((s) => {
                const c = classifySpecial(s);
                return { s, c };
            })
            .filter((r) => r.c)
            .sort((a, b) => {
                // BOGO first (highest value), then % Off, then everything else
                const rank = (k) =>
                    k === "BOGO" ? 0 : k === "PCT_OFF" ? 1 : k === "BUY_FOR" ? 2 : 3;
                return rank(a.c.kind) - rank(b.c.kind);
            });
    }, [specials]);

    return (
        <>
            <SEOHead
                title="All Specials — ZenZest Cannabis Deals"
                description="Every active deal at ZenZest. BOGO offers, mix-and-match bundles, percent-off promos, and limited-time drops. Updated in real time from our point of sale."
                canonical={`https://weeddispensarynearme.com/menu/${storeSlug}/specials`}
            />

            {/* Header */}
            <section className="bg-zen-surface border-b border-zen-ink/10">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <Link
                            to={`/menu/${storeSlug}`}
                            data-testid="specials-back"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink"
                        >
                            <ArrowLeft size={12} /> Back to menu
                        </Link>
                        <StoreSwitcher currentSlug={storeSlug} />
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4 inline-flex items-center gap-2">
                        <Tag size={11} /> All specials
                    </p>
                    <h1
                        data-testid="specials-h1"
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-zen-ink mb-5"
                    >
                        Every deal,{" "}
                        <span className="italic text-zen-olive">in one place.</span>
                    </h1>
                    <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl leading-relaxed">
                        Live from the register at ZenZest. BOGOs, mix-and-match
                        bundles, percent-off vendor drops, and limited-window
                        flash promos — updated in real time.
                    </p>
                </div>
            </section>

            {/* Grid */}
            <section className="bg-zen-bg">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    {err ? (
                        <p
                            data-testid="specials-error"
                            className="text-sm text-zen-ink-soft py-10"
                        >
                            We couldn&rsquo;t load the deals right now. Try refresh.
                        </p>
                    ) : specials === null ? (
                        <p
                            data-testid="specials-loading"
                            className="text-sm text-zen-ink-soft py-10"
                        >
                            Loading deals&hellip;
                        </p>
                    ) : rows.length === 0 ? (
                        <p
                            data-testid="specials-empty"
                            className="text-sm text-zen-ink-soft py-10"
                        >
                            No specials live right now. Check back soon.
                        </p>
                    ) : (
                        <div
                            data-testid="specials-grid"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                        >
                            {rows.map(({ s, c }) => {
                                const img = s.menuDisplayConfiguration?.image;
                                const desc =
                                    s.menuDisplayConfiguration?.description ||
                                    "Tap to see qualifying products and full terms.";
                                return (
                                    <Link
                                        key={s.id}
                                        to={`/menu/${storeSlug}/special/${s.id}`}
                                        data-testid={`special-card-${s.id}`}
                                        className="group block bg-zen-surface border border-zen-ink/10 hover:border-zen-olive transition-colors overflow-hidden"
                                    >
                                        {/* Image area — object-contain so vendor
                                            artwork stays whole. Falls back to a
                                            tag glyph card if Dutchie has none. */}
                                        <div className="aspect-[4/3] bg-zen-bg flex items-center justify-center p-4 border-b border-zen-ink/8">
                                            {img ? (
                                                <img
                                                    src={img}
                                                    alt={s.name}
                                                    loading="lazy"
                                                    className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
                                                />
                                            ) : (
                                                <Tag
                                                    size={48}
                                                    strokeWidth={1.1}
                                                    className="text-zen-olive opacity-60"
                                                />
                                            )}
                                        </div>
                                        <div className="p-5">
                                            <p className="text-[10px] uppercase tracking-[0.25em] text-zen-olive mb-3">
                                                {kindLabel(c.kind)}
                                            </p>
                                            <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-2 line-clamp-2">
                                                {s.name}
                                            </h3>
                                            <p className="text-sm text-zen-ink-soft leading-relaxed line-clamp-3 mb-4">
                                                {desc}
                                            </p>
                                            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 group-hover:gap-2.5 transition-all">
                                                See eligible items <ArrowRight size={11} />
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
